import mapValues from 'lodash-es/mapValues';

import EXPERIMENTS from '@rover/react-lib/src/constants/experiments.constants';
import {
  OVERNIGHT_BOARDING,
  OVERNIGHT_TRAVELING,
} from '@rover/react-lib/src/constants/services.constants';
import {
  petCountersFilters as defaultPetCountersFilters,
  searchFiltersFactory,
} from '@rover/react-lib/src/factories/searchFiltersFactory';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import { selectors as experimentsSelectors } from '@rover/react-lib/src/redux/ducks/experiments.duck';
import { selectors as frontendConfigurationSelectors } from '@rover/react-lib/src/redux/ducks/frontendConfiguration.duck';
import { selectors as petsSelectors } from '@rover/react-lib/src/redux/ducks/pets.duck';
import { selectors as searchSelectors } from '@rover/react-lib/src/redux/ducks/search.duck';
import { selectors as serviceTypeSelectors } from '@rover/react-lib/src/redux/ducks/serviceTypes.duck';
import { FLAGS } from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import {
  DATE_FIELDS,
  NUMBER_FIELDS,
  SEARCH_SOURCE,
} from '@rover/shared/js/constants/searchPage.constants';
import getIsCatOnlyPetType from '@rover/shared/js/search/utilities/getIsCatOnlyPetType';
import type {
  Pet,
  SearchFilters,
  SearchFiltersOptions,
  ServiceType,
  ServiceTypeOption,
} from '@rover/types';

import parseDate from './utilities/parseDate';
import {
  updatePetCountersFromOwnPets,
  updatePetCountersFromPetTypeQueryParam,
} from './utilities/updatePetCounters';
import {
  advancedFiltersCounter,
  filtersToQuery,
  memberProfileFiltersToLinkParams,
  queryToFilters,
} from './utilities';

export type State = {
  filters: SearchFilters;
  didFiltersChangeSincePageLoad: boolean;
  isDirty: boolean;
};
type Actions = {
  filtersUpdated: ActionCreator<{
    filters: Partial<SearchFilters>;
    isInit?: boolean;
  }>;
  dirtyCleared: ActionCreator<void>;
};
type Effects = {
  initFiltersFromQueryParamsWithPetCounters: EffectCreator<void>;
  initFiltersFromQueryParams: EffectCreator<void>;
  updateFilters: EffectCreator<Partial<SearchFilters> & { changeSource?: string }>;
  clearDirty: EffectCreator<void>;
};
// SearchFiltersSelectors is pulled out from the rest of the selectors
// to differentiate selectors that relate only to determining
// visibility of filter components. This is entirely to support
// the SearchModal Duck, which copies these selectors.
export type SearchFiltersSelectors = {
  getFilters: SelectorCreator<SearchFilters>;
  getIsCatOnly: SelectorCreator<boolean>;
  getLocationFilter: SelectorCreator<string>;
  getPetTypeBrowsableServiceTypeOptions: SelectorCreator<ServiceTypeOption[]>;
  getServiceTypeFilter: SelectorCreator<string>;
  getServiceTypeFilterOptions: SelectorCreator<SearchFiltersOptions>;
};
type Selectors = SearchFiltersSelectors & {
  getAdvancedFiltersCount: SelectorCreator<number>;
  getHasBothDates: SelectorCreator<boolean>;
  getHasDayOfWeek: SelectorCreator<boolean>;
  getHasStartDate: SelectorCreator<boolean>;
  getIsDateConstrained: SelectorCreator<boolean>;
  getMemberProfileLinkParams: SelectorCreator<Record<string, string | undefined>>;
  getQuery: SelectorCreator<SearchFilters>;
  getDidFiltersChangeSincePageLoad: SelectorCreator<boolean>;
  showDateConstrainedPrompt: SelectorCreator<boolean>;
  getPetsListForPetCount: SelectorCreator<{
    shouldUpdatePetCount: boolean;
    pets: Pet[];
  }>;
};
type Options = DuckOptions<State, Actions, Effects, Selectors>;

const hasNonPageChange = (filters, oldFilters) => {
  const cloneNew = { ...filters, page: 0 };
  const cloneOld = { ...oldFilters, page: 0 };
  return Object.keys(cloneNew).some(
    (key) => JSON.stringify(cloneNew[key]) !== JSON.stringify(cloneOld[key])
  );
};

export const options: Options = {
  name: 'searchFilters',
  initialState: {
    filters: searchFiltersFactory(),
    didFiltersChangeSincePageLoad: false,
    isDirty: false,
  },
  actions: {
    filtersUpdated:
      ({ filters, isInit }) =>
      (state) => ({
        ...state,
        filters: { ...state.filters, ...filters },
        didFiltersChangeSincePageLoad: !isInit && true,
        ...(!isInit &&
          hasNonPageChange(filters, state.filters) && {
            isDirty: true,
          }),
      }),
    dirtyCleared: () => (state) => ({ ...state, isDirty: false }),
  },
  effects: {
    initFiltersFromQueryParamsWithPetCounters:
      () =>
      async (
        dispatch,
        getState,
        { filtersUpdated },
        duckEffects,
        duckSelectors,
        { query: queryParams }
      ) => {
        const state = getState();
        const serverQuery = searchSelectors.getSearchQuery(state);
        const language = frontendConfigurationSelectors.getLanguage(state);
        const pets = petsSelectors.getPets(state);
        const filters = queryToFilters(queryParams || {}, serverQuery || {}, language);
        const petCount =
          pets.length > 0
            ? updatePetCountersFromOwnPets(filters, pets)
            : updatePetCountersFromPetTypeQueryParam(filters);

        await dispatch(
          filtersUpdated({
            filters: {
              ...filters,
              ...defaultPetCountersFilters,
              ...petCount,
            },
            isInit: true,
          })
        );
      },
    initFiltersFromQueryParams:
      () =>
      async (
        dispatch,
        getState,
        { filtersUpdated },
        duckEffects,
        duckSelectors,
        { query: queryParams }
      ) => {
        const state = getState();
        const serverQuery = searchSelectors.getSearchQuery(state);
        const language = frontendConfigurationSelectors.getLanguage(state);
        const filters = queryToFilters(queryParams || {}, serverQuery || {}, language);

        await dispatch(
          filtersUpdated({
            filters: { ...filters, ...defaultPetCountersFilters },
            isInit: true,
          })
        );
      },
    updateFilters:
      (filters) =>
      async (dispatch, getState, { filtersUpdated }, duckEffects, { getPetsListForPetCount }) => {
        const state = getState();
        const { shouldUpdatePetCount, pets } = getPetsListForPetCount(state);
        let petCount = {};

        if (shouldUpdatePetCount && pets.length > 0) {
          petCount = updatePetCountersFromOwnPets(filters, pets);
        }

        if (shouldUpdatePetCount && filters?.changeSource === SEARCH_SOURCE.SEARCH_MODAL) {
          petCount = updatePetCountersFromPetTypeQueryParam(filters);
        }

        await dispatch(
          filtersUpdated({
            filters: {
              page: 1,
              ...filters,
              ...petCount,
            },
          })
        );
      },
    clearDirty:
      () =>
      async (dispatch, getState, { dirtyCleared }) => {
        await dispatch(dirtyCleared());
      },
  },
  selectors: {
    getFilters: (getState, createSelector) =>
      createSelector([getState, frontendConfigurationSelectors.getLanguage], (x, language) => {
        return mapValues(x.filters, (value, key) => {
          // when we initialize the filters on the server
          // when they get stringified it turns fields that are
          // date objects into strings, so we convert them back here
          if (DATE_FIELDS.has(key) && value) {
            return parseDate(value, language);
          }

          // same here for numeric fields
          if (NUMBER_FIELDS.has(key) && value) {
            return parseInt(value, 10);
          }

          return value;
        });
      }),
    getDidFiltersChangeSincePageLoad: (getState, createSelector) =>
      createSelector([getState], (x) => x.didFiltersChangeSincePageLoad),
    getServiceTypeFilter: (getState, createSelector, { getFilters }) =>
      createSelector([getFilters], (filters) => filters.serviceType),
    getLocationFilter: (getState, createSelector, { getFilters }) =>
      createSelector([getFilters], (filters) => filters.location),
    getQuery: (getState, createSelector) =>
      createSelector(
        [
          getState,
          serviceTypeSelectors.getServiceTypeBySlug,
          frontendConfigurationSelectors.getLanguage,
        ],
        (x, getServiceTypeBySlug, language) =>
          filtersToQuery(
            x.filters,
            getServiceTypeBySlug(x.filters.serviceType),
            x.isDirty,
            language
          )
      ),
    getHasBothDates: (getState, createSelector, { getFilters }) =>
      createSelector([getFilters], ({ endDate, startDate }) => !!(endDate && startDate)),
    getHasDayOfWeek: (getState, createSelector, { getFilters }) =>
      createSelector([getFilters], ({ selectedDays }) => !!(selectedDays && selectedDays.length)),
    getHasStartDate: (getState, createSelector, { getFilters }) =>
      createSelector([getFilters], ({ startDate }) => !!startDate),
    getIsDateConstrained: (
      getState,
      createSelector,
      { getFilters, getHasBothDates, getHasDayOfWeek, getHasStartDate }
    ) =>
      createSelector(
        [
          getFilters,
          getHasBothDates,
          getHasDayOfWeek,
          getHasStartDate,
          serviceTypeSelectors.getServiceTypeBySlug,
        ],
        (
          { serviceType, frequency },
          hasBothDates,
          hasDayOfWeek,
          hasStartDate,
          getServiceTypeBySlug
        ) => {
          const serviceTypeObj = getServiceTypeBySlug(serviceType);

          if (serviceTypeObj && serviceTypeObj.supportsRepeatBookings) {
            const isRepeatDateConstrained = frequency === 'repeat' && hasDayOfWeek && hasStartDate;
            const isOnetimeDateConstrained = frequency === 'onetime' && hasBothDates;
            return isRepeatDateConstrained || isOnetimeDateConstrained;
          }

          return hasBothDates;
        }
      ),
    showDateConstrainedPrompt: (getState, createSelector, { getFilters }) =>
      createSelector(
        [getFilters, frontendConfigurationSelectors.getIsBeforeHoliday],
        ({ serviceType, frequency, endDate, startDate }, isBeforeHoliday) =>
          (serviceType === OVERNIGHT_BOARDING().type ||
            serviceType === OVERNIGHT_TRAVELING().type ||
            frequency === 'onetime') &&
          (!endDate || !startDate) &&
          isBeforeHoliday
      ),
    getAdvancedFiltersCount: (getState, createSelector) =>
      createSelector([getState, searchSelectors.getSearchFiltersOptions], (x, searchOptions) =>
        advancedFiltersCounter(x.filters, searchOptions[x.filters.serviceType])
      ),
    getServiceTypeFilterOptions: (getState, createSelector, { getServiceTypeFilter }) =>
      createSelector(
        [searchSelectors.getSearchFiltersOptions, getServiceTypeFilter],
        (searchFilterOptions, selectedServiceType) => searchFilterOptions[selectedServiceType] || {}
      ),
    getIsCatOnly: (getState, createSelector) =>
      createSelector([getState], (x) => getIsCatOnlyPetType(x.filters.petType)),
    getMemberProfileLinkParams: (getState, createSelector, { getFilters }) =>
      createSelector(
        [
          getFilters,
          frontendConfigurationSelectors.getLanguage,
          frontendConfigurationSelectors.isFlagActive,
        ],
        (
          {
            serviceType,
            startDate,
            endDate,
            frequency,
            petType,
            selectedDays,
            dogCount,
            catCount,
            puppyCount,
          },
          language,
          isFlagActive
        ) => {
          const rolloutPriceTransparency = isFlagActive(
            FLAGS.ROLLOUT_CALIFORNIA_PRICE_TRANSPARENCY
          );
          return memberProfileFiltersToLinkParams(
            {
              serviceType,
              startDate,
              endDate,
              frequency,
              petType,
              selectedDays,
              ...(rolloutPriceTransparency ? { dogCount, catCount, puppyCount } : {}),
            },
            language
          );
        }
      ),
    getPetTypeBrowsableServiceTypeOptions: (getState, createSelector) =>
      createSelector(
        [
          getState,
          serviceTypeSelectors.getServiceTypes,
          serviceTypeSelectors.getPetTypeBrowsableServiceTypes,
        ],
        (x, serviceTypes, getPetTypeBrowsableServices) => {
          const petTypeBrowsableServices = getPetTypeBrowsableServices(x.filters.petType);

          const uniqueMapped = (services: ServiceType[]) =>
            [...new Set(services)].map((s) => ({
              title: s.displayName,
              subtitle: s.description,
              value: s.slug,
            }));

          return uniqueMapped(petTypeBrowsableServices);
        }
      ),
    getPetsListForPetCount: (getState, createSelector) =>
      createSelector(
        [
          experimentsSelectors.isVariant,
          petsSelectors.getPets,
          frontendConfigurationSelectors.isFlagActive,
        ],
        (isVariant, pets, isFlagActive) => {
          const rolloutPriceTransparency = isFlagActive(
            FLAGS.ROLLOUT_CALIFORNIA_PRICE_TRANSPARENCY
          );
          const isInPetCountersExperiment = isVariant(EXPERIMENTS.PET_COUNTERS_EXPERIMENT);

          return {
            shouldUpdatePetCount: rolloutPriceTransparency || isInPetCountersExperiment,
            pets,
          };
        }
      ),
  },
};
export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
